import { useState } from "react";
import { generateOffer, saveItem } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Sparkles, Crown, Check, X, ArrowRight, Save, ArrowLeft, AlertTriangle, Tag, MessageSquare, Layers, ArrowUpRight, BadgePercent } from "lucide-react";
import { toast } from "sonner";

const COMPANY_SIZES = [
  { v: "freelancer", l: "Freelancer / Solopreneur" },
  { v: "small", l: "Pequeña empresa (1-20)" },
  { v: "startup", l: "Startup (20-100)" },
  { v: "mid", l: "Mediana empresa (100-500)" },
  { v: "multinational", l: "Multinacional (500+)" },
];
const SOPHISTICATION = ["Baja", "Media", "Alta", "Muy alta"];
const URGENCY = ["Baja", "Normal", "Alta", "Crítica"];
const STAGES = ["Nueva", "Establecida", "Refresco", "Cambio de rumbo"];
const NATURE = ["Táctico", "Estratégico", "Mixto"];
const COUNTRIES = [
  { v: "US", l: "🇺🇸 USA / Internacional", currency: "USD", placeholder: "1000, 5000, 20000..." },
  { v: "MX", l: "🇲🇽 México", currency: "MXN", placeholder: "10000, 50000, 250000..." },
  { v: "AR", l: "🇦🇷 Argentina", currency: "ARS", placeholder: "500000, 2000000..." },
  { v: "BR", l: "🇧🇷 Brasil", currency: "BRL", placeholder: "3000, 15000, 60000..." },
  { v: "ES", l: "🇪🇸 España", currency: "EUR", placeholder: "800, 3000, 15000..." },
];

const fieldBase = "mt-2 bg-white rounded-2xl h-12 border border-black/10 focus-visible:ring-brand-purple";
const taBase = "mt-2 bg-white rounded-2xl border border-black/10 resize-none focus-visible:ring-brand-purple";
const lbl = "text-sm font-semibold text-foreground/85";

function FormView({ form, setForm, onGenerate, loading, errorField }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const errCls = (k) => (errorField === k ? "!border-rose-500 ring-1 ring-rose-500/40" : "");

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-10 relative">
        <div className="blob blob-mint w-[200px] h-[200px] -top-6 -left-10" />
        <div className="blob blob-lavender w-[200px] h-[200px] top-0 right-1/4" style={{ animationDelay: "2s" }} />
        <div className="relative">
          <span className="tag-pill tag-pill-mint mb-5"><Layers className="w-3 h-3" /> Crear oferta</span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.04em] leading-[0.95]">
            Construye una oferta<br />
            que <span className="scribble-underline scribble-coral">escale valor</span>.
          </h1>
          <p className="text-base text-foreground/65 mt-5 max-w-2xl leading-relaxed">
            Cuéntame el contexto y te entrego 4 packs con precios, riesgos, upsells y el mensaje listo para enviar al cliente.
          </p>
        </div>
      </header>

      <div className="app-card p-7 md:p-9 space-y-6">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className={lbl}>Servicio <span className="text-brand-purple">*</span></Label>
            <Input id="field-service-type" data-testid="input-service-type" value={form.service_type} onChange={upd("service_type")} placeholder="Identidad de marca, diseño web, content strategy..." className={`${fieldBase} ${errCls("service_type")}`} />
            {errorField === "service_type" && <p className="text-xs text-rose-600 mt-1.5 font-medium">Cuéntame qué tipo de servicio vas a vender.</p>}
          </div>
          <div>
            <Label className={lbl}>Industria</Label>
            <Input data-testid="input-industry" value={form.industry} onChange={upd("industry")} placeholder="SaaS, retail, fintech..." className={fieldBase} />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <Label className={lbl}>Tipo de cliente</Label>
            <Select value={form.company_size} onValueChange={upd("company_size")}>
              <SelectTrigger data-testid="select-company-size" className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{COMPANY_SIZES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Facturación</Label>
            <Input data-testid="input-revenue" value={form.revenue_range} onChange={upd("revenue_range")} placeholder="<1M, 1-10M..." className={fieldBase} />
          </div>
          <div>
            <Label className={lbl}>Tamaño del equipo</Label>
            <Input data-testid="input-team" value={form.team_size} onChange={upd("team_size")} placeholder="5, 20..." className={fieldBase} />
          </div>
        </div>

        <div>
          <Label className={lbl}>Objetivos del cliente</Label>
          <Textarea data-testid="input-objectives" rows={3} value={form.objectives} onChange={upd("objectives")} placeholder="Lanzar producto, reposicionarse, atraer inversión..." className={taBase} />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className={lbl}>País del cliente <span className="text-brand-purple">*</span></Label>
            <Select value={form.country} onValueChange={upd("country")}>
              <SelectTrigger data-testid="select-country" className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l} · {o.currency}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-foreground/55 mt-1.5">Los precios se ajustan al mercado local</p>
          </div>
          <div>
            <Label className={lbl}>Presupuesto ({COUNTRIES.find((c) => c.v === form.country)?.currency || "USD"})</Label>
            <Input data-testid="input-budget" value={form.budget} onChange={upd("budget")} placeholder={COUNTRIES.find((c) => c.v === form.country)?.placeholder || "1000..."} className={fieldBase} />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <Label className={lbl}>Urgencia</Label>
            <Select value={form.urgency} onValueChange={upd("urgency")}>
              <SelectTrigger className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{URGENCY.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Sofisticación</Label>
            <Select value={form.brand_sophistication} onValueChange={upd("brand_sophistication")}>
              <SelectTrigger className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{SOPHISTICATION.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Etapa</Label>
            <Select value={form.brand_stage} onValueChange={upd("brand_stage")}>
              <SelectTrigger className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className={lbl}>Naturaleza</Label>
            <Select value={form.project_nature} onValueChange={upd("project_nature")}>
              <SelectTrigger className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{NATURE.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Notas extra</Label>
            <Input data-testid="input-notes" value={form.extra_notes} onChange={upd("extra_notes")} placeholder="Lo que no encaja en los otros campos..." className={fieldBase} />
          </div>
        </div>

        <button
          data-testid="generate-offer-btn"
          onClick={onGenerate}
          disabled={loading}
          className="btn-apple w-full justify-center text-base h-14"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando...</> : <><Sparkles className="w-4 h-4" /> Generar oferta estratégica <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}

function ResultView({ data, form, onReset, onSave }) {
  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onReset} className="btn-apple-outline text-sm" data-testid="back-to-form-offer">
          <ArrowLeft className="w-3.5 h-3.5" /> Editar datos
        </button>
        <button onClick={onSave} className="btn-apple text-sm" data-testid="save-offer-btn">
          <Save className="w-3.5 h-3.5" /> Guardar en biblioteca
        </button>
      </div>

      {/* Hero del resultado */}
      <div className="app-card-dark p-10 relative">
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#A8D5BA]/15 blur-2xl" />
        <div className="text-[11px] uppercase tracking-wider font-bold opacity-65 relative">Pack recomendado</div>
        <h2 className="font-display text-4xl md:text-6xl font-black tracking-[-0.04em] mt-3 relative" data-testid="result-tier">{data.recommended_tier}</h2>
        <p className="mt-4 text-base md:text-lg max-w-2xl opacity-85 leading-relaxed relative">{data.positioning_statement}</p>
      </div>

      <Tabs defaultValue="tiers" className="w-full">
        <TabsList className="bg-white border border-black/[0.08] rounded-2xl p-1 grid grid-cols-2 md:grid-cols-5 h-auto">
          <TabsTrigger value="tiers" data-testid="tab-tiers" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <Layers className="w-3.5 h-3.5 mr-2" /> Packs
          </TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <Tag className="w-3.5 h-3.5 mr-2" /> Precio
          </TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <AlertTriangle className="w-3.5 h-3.5 mr-2" /> Riesgos
          </TabsTrigger>
          <TabsTrigger value="discount" data-testid="tab-discount" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <BadgePercent className="w-3.5 h-3.5 mr-2" /> Rebaja
          </TabsTrigger>
          <TabsTrigger value="message" data-testid="tab-message" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <MessageSquare className="w-3.5 h-3.5 mr-2" /> Mensaje
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tiers" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 items-stretch">
            {data.tiers?.map((t, i) => {
              const featured = t.name === data.recommended_tier;
              return (
                <div key={i} className={`relative ${featured ? "ring-2 ring-foreground ring-offset-4 ring-offset-background rounded-3xl" : ""}`}>
                  {featured && <div className="absolute -top-3 left-6 bg-foreground text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider z-20">Recomendado</div>}
                  <div className={`${featured ? "app-card-dark" : "app-card"} p-6 h-[640px] overflow-y-auto custom-scroll`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold opacity-55">0{i + 1}</span>
                        <h3 className="font-display text-xl font-black tracking-[-0.02em]">{t.name}</h3>
                        {featured && <Crown className="w-4 h-4" strokeWidth={2} />}
                      </div>
                      <span className={`tag-pill ${featured ? "tag-pill-dark" : ""} text-[10px]`}>{t.complexity_level}</span>
                    </div>
                    <p className="text-sm opacity-75 mb-4">{t.subtitle}</p>
                    <div className="font-display text-3xl font-black tracking-[-0.02em] mb-4">{t.price_range}</div>

                    <div className={`grid grid-cols-3 gap-2 text-[11px] opacity-80 border-y ${featured ? "border-white/15" : "border-black/[0.08]"} py-3 mb-4`}>
                      <div><div className="opacity-55">Plazo</div><div className="font-semibold mt-0.5">{t.timeline}</div></div>
                      <div><div className="opacity-55">Revisiones</div><div className="font-semibold mt-0.5">{t.revisions}</div></div>
                      <div><div className="opacity-55">Soporte</div><div className="font-semibold mt-0.5">{t.support_level}</div></div>
                    </div>

                    <p className="text-xs opacity-65 mb-4">Ideal para: {t.ideal_for}</p>

                    <div className="space-y-1.5 mb-4">
                      <div className="text-[10px] font-bold uppercase tracking-wider opacity-55">Incluye</div>
                      {t.includes?.map((x, j) => (
                        <div key={j} className="text-xs flex gap-2"><Check className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.5} /> {x}</div>
                      ))}
                    </div>
                    <div className="space-y-1.5 mb-4">
                      <div className="text-[10px] font-bold uppercase tracking-wider opacity-40">No incluye</div>
                      {t.excludes?.map((x, j) => (
                        <div key={j} className="text-xs flex gap-2 opacity-55"><X className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.5} /> {x}</div>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider opacity-55">Resultados estratégicos</div>
                      {t.strategic_outcomes?.map((x, j) => (
                        <div key={j} className="text-xs flex gap-2 font-medium"><ArrowRight className="w-3 h-3 mt-0.5" strokeWidth={2.5} /> {x}</div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 items-stretch">
            <div className="app-card p-7 h-[340px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-mint mb-4">Lógica del precio</div>
              <p className="text-sm leading-relaxed">{data.pricing_logic}</p>
            </div>
            <div className="app-card p-7 h-[340px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-rose mb-4">Reglas anti sobretrabajo</div>
              <ul className="space-y-3">
                {data.anti_scope_creep?.map((x, i) => (
                  <li key={i} className="text-sm flex gap-3"><span className="font-display font-black text-foreground/40 shrink-0">0{i + 1}</span> {x}</li>
                ))}
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="risks" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 items-stretch">
            <div className="app-card p-7 h-[380px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-rose mb-4"><AlertTriangle className="w-3 h-3" strokeWidth={2.5} /> Riesgos</div>
              <ul className="space-y-2.5">
                {data.risks?.map((x, i) => <li key={i} className="text-sm flex gap-2"><span className="text-foreground/40 mt-0.5">—</span>{x}</li>)}
              </ul>
            </div>
            <div className="app-card p-7 h-[380px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-mint mb-4"><ArrowUpRight className="w-3 h-3" strokeWidth={2.5} /> Upsells</div>
              <div className="space-y-4 mt-2">
                {data.upsells?.map((u, i) => (
                  <div key={i} className="border-l-2 border-foreground/15 pl-3">
                    <div className="text-sm font-bold flex items-center gap-2 flex-wrap">{u.name} <span className="font-display text-xs text-foreground/55">{u.price_range}</span></div>
                    <div className="text-xs text-foreground/65 mt-1">{u.why}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="discount" className="mt-6">
          {data.discount_response ? (
            <div className="app-card p-8 relative">
              <div className="tag-pill tag-pill-amber mb-4"><BadgePercent className="w-3 h-3" /> Respuesta lista para enviar</div>
              <h3 className="font-display text-2xl font-black tracking-[-0.02em] mb-2">Si tu cliente te pide rebaja…</h3>
              <p className="text-sm text-foreground/65 mb-5">Copia este texto y mándaselo. Sin pedir perdón, sin regalar trabajo.</p>
              <div className="bg-[#FAFAF7] rounded-2xl p-6 border border-black/[0.06] relative">
                <p className="text-[15px] leading-relaxed whitespace-pre-wrap font-medium">{data.discount_response}</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(data.discount_response); toast.success("Copiado"); }}
                  className="absolute top-3 right-3 btn-apple-outline text-xs px-3 py-1.5"
                  data-testid="copy-discount-btn"
                >
                  <ArrowUpRight className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
            </div>
          ) : (
            <div className="app-card p-8 text-center">
              <p className="text-sm text-foreground/65">Esta oferta no incluye respuesta a rebaja.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="message" className="mt-6">
          {data.premium_language && (
            <div className="app-card p-8">
              <div className="tag-pill tag-pill-lavender mb-5">Mensaje premium listo para enviar</div>
              <div className="space-y-5">
                <div className="border-b border-black/[0.06] pb-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">Asunto</span>
                  <p className="mt-1 font-bold text-lg">{data.premium_language.subject}</p>
                </div>
                <div className="border-b border-black/[0.06] pb-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">Apertura</span>
                  <p className="mt-1 leading-relaxed">{data.premium_language.opening}</p>
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">Encuadre de valor</span>
                  <p className="mt-1 leading-relaxed text-foreground/80">{data.premium_language.value_framing}</p>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function OfferBuilder() {
  const [form, setForm] = useState({
    service_type: "",
    industry: "",
    company_size: "small",
    revenue_range: "",
    team_size: "",
    objectives: "",
    urgency: "Normal",
    budget: "",
    country: "US",
    brand_sophistication: "Media",
    brand_stage: "Nueva",
    project_nature: "Estratégico",
    extra_notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [errorField, setErrorField] = useState(null);

  const onGenerate = async () => {
    if (!form.service_type?.trim()) {
      setErrorField("service_type");
      toast.error("Cuéntame qué tipo de servicio vas a vender");
      setTimeout(() => {
        const el = document.getElementById("field-service-type");
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
      }, 100);
      return;
    }
    setErrorField(null);
    setLoading(true);
    try {
      const r = await generateOffer(form);
      setData(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("¡Oferta lista!");
    } catch (e) {
      console.error(e);
      toast.error(e.userMessage || "Error generando la oferta. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    if (!data) return;
    try {
      await saveItem({ kind: "offer", title: `${form.service_type} · ${form.company_size}`, payload: { input: form, output: data } });
      toast.success("Guardado en biblioteca");
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const onReset = () => { setData(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center relative">
        <div className="blob blob-mint w-[300px] h-[300px] top-0 left-1/3" />
        <div className="blob blob-lavender w-[300px] h-[300px] bottom-0 right-1/3" style={{ animationDelay: "2s" }} />
        <div className="text-center max-w-sm relative">
          <div className="w-14 h-14 mx-auto rounded-full bg-foreground grid place-items-center mb-6">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
          <h2 className="font-display text-2xl font-black tracking-[-0.02em]">Construyendo tu oferta...</h2>
          <p className="text-sm text-foreground/65 mt-3">Diseñando 4 packs, precios, riesgos y el mensaje al cliente.</p>
        </div>
      </div>
    );
  }

  if (data) return <ResultView data={data} form={form} onReset={onReset} onSave={onSave} />;
  return <FormView form={form} setForm={setForm} onGenerate={onGenerate} loading={loading} errorField={errorField} />;
}
